import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchEvents } from '../lib/cms';

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadEvents() {
      const data = await fetchEvents();
      // Sort events by date ascending
      const sorted = (data || []).sort((a, b) => new Date(a.date) - new Date(b.date));
      setEvents(sorted);
      setLoading(false);
    }
    loadEvents();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-sand-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-clay-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-sand-50 text-foreground min-h-screen">
      {/* Header Banner */}
      <section className="relative py-20 md:py-28 bg-[#60b6c0] text-sand-50 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/img-4488.jpg?width=1920&quality=60&format=auto"
            alt="Arrangementer"
            className="h-full w-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center flex flex-col gap-4">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tracking-wide leading-tight">
            Arrangementer & Workshops
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-base text-white leading-relaxed">
            Deltag i Heidis workshops, kurser og koncerter. Find ro, styrk din stemme og mærk fællesskabet.
          </p>
        </div>
      </section>

      {/* Events List Sektion */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          {events.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-3xl border border-sand-200 p-8">
              <i className="fa-light fa-calendar-xmark text-4xl text-sand-400 mb-4" />
              <h3 className="font-serif text-xl font-bold text-black">Ingen planlagte arrangementer</h3>
              <p className="text-sm text-foreground/60 mt-2">Der er i øjeblikket ingen kommende arrangementer. Hold øje med siden eller kontakt Heidi for at høre mere.</p>
              <Link to="/kontakt" className="mt-6 inline-block rounded-full bg-cta px-6 py-2.5 text-xs uppercase tracking-widest font-semibold text-clay-900 hover:bg-cta-hover transition-all">Kontakt Heidi</Link>
            </div>
          ) : (
            <div className="flex flex-col gap-10">
              {events.map((event) => {
                const eventDate = new Date(event.date);
                const formattedDate = eventDate.toLocaleDateString('da-DK', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                });

                return (
                  <div
                    key={event.id}
                    className="flex flex-col md:flex-row bg-white rounded-none border border-sand-200/60 shadow-sm overflow-hidden transition-all hover:shadow-md"
                  >
                    {/* Event Image */}
                    <div className="w-full md:w-1/3 h-56 md:h-auto relative shrink-0">
                      <img
                        src={`${event.image?.[0]}?width=500&height=400&fit=cover&format=auto`}
                        alt={event.title.da}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Event Details */}
                    <div className="p-6 md:p-8 w-full md:w-2/3 flex flex-col justify-between gap-6 text-left">
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-sage-600 font-semibold uppercase tracking-wider">
                          <span className="flex items-center gap-1.5">
                            <i className="fa-light fa-calendar" />
                            {formattedDate}
                          </span>
                          {event.time?.da && (
                            <>
                              <span className="text-sand-300">•</span>
                              <span className="flex items-center gap-1.5">
                                <i className="fa-light fa-clock" />
                                {event.time.da}
                              </span>
                            </>
                          )}
                        </div>

                        <h2 className="font-serif text-2xl md:text-3xl font-bold text-black">
                          {event.title.da}
                        </h2>

                        {event.location?.da && (
                          <p className="text-xs md:text-sm text-foreground/60 flex items-center gap-2">
                            <i className="fa-light fa-location-dot text-clay-500" />
                            {event.location.da}
                          </p>
                        )}

                        <div
                          className="text-sm md:text-base text-foreground/75 leading-relaxed mt-2 prose prose-sand max-w-none prose-a:text-clay-700 prose-a:underline hover:prose-a:text-black"
                          dangerouslySetInnerHTML={{ __html: event.description?.da }}
                        />
                      </div>

                      {/* Footer Info */}
                      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-sand-100 pt-6">
                        <div className="flex flex-col">
                          <span className="text-xs text-foreground/50 uppercase tracking-wider">Pris</span>
                          <span className="text-lg font-bold text-black">
                            {event.price?.da || 'Gratis'}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          {event.link && (
                            <a
                              href={event.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-white hover:bg-sand-100 text-black px-5 py-2.5 text-xs uppercase tracking-widest font-semibold transition-all shadow-xs"
                            >
                              <i className="fa-brands fa-facebook text-[#1877F2] text-sm" aria-hidden="true" />
                              <span>Se opslaget hos Solvognen på Facebook</span>
                              <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-foreground/50" aria-hidden="true" />
                            </a>
                          )}
                          {event.booking_link ? (
                            <a
                              href={event.booking_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded-full bg-cta hover:bg-cta-hover text-clay-900 px-6 py-2.5 text-xs uppercase tracking-widest font-semibold transition-all active:scale-98 shadow-sm inline-flex items-center gap-1.5"
                            >
                              <span>Bestil billet</span>
                              <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                            </a>
                          ) : (
                            <Link
                              to="/kontakt"
                              className="rounded-full bg-cta hover:bg-cta-hover text-clay-900 px-6 py-2.5 text-xs uppercase tracking-widest font-semibold transition-all active:scale-98 shadow-sm"
                            >
                              Tilmeld mig her <i className="fa-solid fa-chevron-right text-[9px] ml-1" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
